import ProductTile from '@moncler/components/listing/ProductTile';
import { ListingActionTypes } from '@state/features/common.types';
import analytics from '@utils/analytics';
import JsonLd from '@utils/JsonLd';
import { getProductAvailability } from '@utils/product';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { getFirstFocusable } from '@common/scripts/utilities/focusableElementUtils';
import whatInput from 'what-input';
import EditorialCard from './EditorialCard';
import PromoEditorialCard from './PromoEditorialCard';
import PromoBanner from './PromoBanner';
import PromoShowcase from './PromoShowcase';
import EditorialText from './EditorialText';
import RichVideo from './RichVideo';
import Promo5050 from './Promo5050';
import SlimBanner from './SlimBanner';
import customEventBus from '@common/scripts/libs/eventBus';
export const editorialCardTypeMap = {
    component_plp_highlight_product: () => 'plppromo--2x2',
    component_promo_showcase: () => 'plppromo--2x2',
    component_plp_promo_card_large: () => 'plppromo--2x2 plppromo__promo-large',
    component_plp_promo_banner: () => 'plppromo--2x2 plppromo__promo-banner',
    component_plp_promo_card_medium: () => 'plppromo--2x1 plppromo__promo-medium',
    component_plp_promo_card_small: () => 'plppromo plppromo__promo-small',
    component_promo_50_50: () => 'promo-50-50',
    component_plp_editorial_text: () => 'editorial-text--4x1',
    component_rich_video: (dimension) => `rich-video rich-video--${dimension}`,
    component_plppromo: (dimension) => `plppromo--${dimension}`,
    component_slim_banner: () => 'slim-banner editorial-text--4x1',
};
export const EditorialPromoCardTypeMap = {
    component_plp_promo_card_large: PromoEditorialCard,
    component_plp_promo_card_medium: PromoEditorialCard,
    component_plp_promo_card_small: PromoEditorialCard,
    component_plp_promo_banner: PromoBanner,
    component_slim_banner: SlimBanner,
    component_promo_showcase: PromoShowcase,
    component_promo_50_50: Promo5050,
    component_plp_editorial_text: EditorialText,
    component_rich_video: RichVideo,
};
export const editorialCardTextColorMap = {
    component_plp_promo_card_large: 'white-title',
    component_plp_promo_card_medium: 'white-title',
    component_plp_promo_card_small: 'white-title',
    component_plp_promo_banner: 'white-title',
};
export const cardSpace = {
    component_plp_highlight_product: () => 4,
    component_promo_showcase: () => 4,
    component_plp_promo_card_large: () => 4,
    component_slim_banner: () => 4,
    component_plp_promo_card_medium: () => 2,
    component_promo_50_50: () => 4,
    component_plp_editorial_text: () => 4,
    component_rich_video: (dimension) => (dimension === 'landscape' ? 4 : 4),
    component_plppromo: (dimension) => (dimension === '2x2' ? 4 : 2),
};
export const extraAdded = {
    component_plp_highlight_product: () => 2,
    component_promo_showcase: () => 2,
    component_plp_promo_card_large: () => 2,
    component_rich_video: (dimension) => (dimension === 'landscape' ? 0 : 2),
    component_plppromo: (dimension) => (dimension === '2x2' ? 2 : 0),
};
const ListingResults = (props) => {
    const { t } = useTranslation();
    const { results, progress, count, selectedFilters, editorialcards, pageMetaTags, pageSize, pageNumber, category, landingAnimation, productSort, breadcrumbs } = useSelector((state) => state.search);
    const isLoading = useSelector((state) => state.loading).requests.includes('SEARCH');
    const [isKeyboardIntent, setIsKeyboardIntent] = useState(false);
    const { apiErrors } = useSelector((state) => state.errors);
    const loadingError = useMemo(() => {
        return apiErrors.find((error) => {
            return error.code === 'PAGE';
        });
    }, [apiErrors]);
    const dispatch = useDispatch();
    const [startAnimation, setStartAnimation] = useState(window.ssr ? true : false);
    useEffect(() => {
        if (landingAnimation) {
            setTimeout(() => {
                setStartAnimation(true);
                // aspetto 550ms (400 + 150) che è il tempo dell'animazione al dom loaded
                // aggiungo 200ms per assicurarmi che le foto siano caricate
            }, 750);
        }
    }, [landingAnimation]);
    useEffect(() => {
        customEventBus.on('keyboard:mode', (data) => {
            var _a;
            // OE-16266 check input element to avoid autocomplete
            if (data.isKeyboardMode && ((_a = data.target) === null || _a === void 0 ? void 0 : _a.tagName) !== 'INPUT') {
                setIsKeyboardIntent(data.isKeyboardMode);
            }
        });
    }, []);
    const [expandedLook, setExpandedLook] = useState();
    const fetchData = () => {
        dispatch({
            type: ListingActionTypes.FETCH_LISTING,
            payload: {
                resultsType: 'PAGE',
                start: progress,
                selectedFilters,
            },
        });
    };
    const hasMore = useMemo(() => {
        return progress < count;
    }, [progress, count]);
    useEffect(() => {
        if (whatInput.ask() == 'keyboard' && pageNumber > 0 && isKeyboardIntent) {
            // console.info('metto il focus');
            setTimeout(() => getFirstFocusable(document.querySelector(`.grid-container li:nth-child(${pageNumber * pageSize + 1})`)).focus(), 500);
        }
    }, [pageNumber]);
    const totalCards = [...results];
    const toAddMarginIndexes = new Map();
    if ((editorialcards === null || editorialcards === void 0 ? void 0 : editorialcards.length) > 0) {
        // console.info(editorialcards);
        editorialcards.forEach((card) => {
            if ((totalCards === null || totalCards === void 0 ? void 0 : totalCards.length) >= card.index) {
                totalCards.splice(card.index, 0, Object.assign(Object.assign(Object.assign({}, card), { type: editorialCardTypeMap[card.typeid](card.dimension), isEditorial: true }), (editorialCardTextColorMap[card.typeid] && { isWhite: true })));
            }
        });
        let cardsCount = 0;
        let credit = 0;
        totalCards.forEach((card, index) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            if (card.typeid === 'component_plp_promo_card_large') {
                switch (card.paddings) {
                    case 'None':
                        break;
                    case 'Top Bottom Right':
                        toAddMarginIndexes.set(index, 'margin-extra-top margin-extra-bottom margin-extra-right');
                        break;
                    case 'Top Bottom Left':
                        toAddMarginIndexes.set(index, 'margin-extra-top margin-extra-bottom margin-extra-left');
                        break;
                    case 'Top Bottom':
                        toAddMarginIndexes.set(index, 'margin-extra-top margin-extra-bottom');
                        break;
                }
                if (card.paddings && card.paddings !== 'None') {
                    if ((cardsCount - credit) % 4 === 0) {
                        if (!(extraAdded[(_a = totalCards[index + 1]) === null || _a === void 0 ? void 0 : _a.typeid] && extraAdded[(_b = totalCards[index + 1]) === null || _b === void 0 ? void 0 : _b.typeid](card.dimension))) {
                            if (((_c = totalCards[index + 1]) === null || _c === void 0 ? void 0 : _c.typeid) === 'component_plp_promo_card_medium' && ((_d = totalCards[index + 1]) === null || _d === void 0 ? void 0 : _d.typeid) === 'component_plppromo') {
                                toAddMarginIndexes.set(index + 1, 'margin-extra-top');
                            }
                            else {
                                toAddMarginIndexes.set(index + 1, 'margin-extra-top');
                                toAddMarginIndexes.set(index + 2, 'margin-extra-top');
                            }
                            if (((_e = totalCards[index + 2]) === null || _e === void 0 ? void 0 : _e.typeid) === 'component_plp_promo_card_medium' && ((_f = totalCards[index + 2]) === null || _f === void 0 ? void 0 : _f.typeid) === 'component_plppromo') {
                                toAddMarginIndexes.set(index + 2, 'margin-extra-bottom');
                            }
                            else if (((_g = totalCards[index + 3]) === null || _g === void 0 ? void 0 : _g.typeid) === 'component_plp_promo_card_medium' && ((_h = totalCards[index + 3]) === null || _h === void 0 ? void 0 : _h.typeid) === 'component_plppromo') {
                                toAddMarginIndexes.set(index + 3, 'margin-extra-bottom');
                            }
                            else {
                                toAddMarginIndexes.set(index + 3, 'margin-extra-bottom');
                                toAddMarginIndexes.set(index + 4, 'margin-extra-bottom');
                            }
                        }
                        else {
                            toAddMarginIndexes.set(index + 1, 'margin-extra-top margin-extra-bottom');
                        }
                    }
                    else if ((cardsCount - credit) % 2 === 0) {
                        if (!(extraAdded[(_j = totalCards[index - 1]) === null || _j === void 0 ? void 0 : _j.typeid] && extraAdded[(_k = totalCards[index - 1]) === null || _k === void 0 ? void 0 : _k.typeid](card.dimension))) {
                            if (((_l = totalCards[index - 1]) === null || _l === void 0 ? void 0 : _l.typeid) === 'component_plp_promo_card_medium' && ((_m = totalCards[index - 1]) === null || _m === void 0 ? void 0 : _m.typeid) === 'component_plppromo') {
                                toAddMarginIndexes.set(index - 1, 'margin-extra-top');
                            }
                            else {
                                toAddMarginIndexes.set(index - 1, 'margin-extra-top');
                                toAddMarginIndexes.set(index - 2, 'margin-extra-top');
                            }
                            if (((_o = totalCards[index + 1]) === null || _o === void 0 ? void 0 : _o.typeid) === 'component_plp_promo_card_medium' && ((_p = totalCards[index + 1]) === null || _p === void 0 ? void 0 : _p.typeid) === 'component_plppromo') {
                                toAddMarginIndexes.set(index + 1, 'margin-extra-bottom');
                            }
                            else {
                                toAddMarginIndexes.set(index + 1, 'margin-extra-bottom');
                                toAddMarginIndexes.set(index + 2, 'margin-extra-bottom');
                            }
                        }
                        else {
                            toAddMarginIndexes.set(index - 1, 'margin-extra-top margin-extra-bottom');
                        }
                    }
                }
            }
            if (cardSpace[card.typeid]) {
                cardsCount += cardSpace[card.typeid](card.dimension);
            }
            else {
                cardsCount += 1;
            }
            if (!credit && extraAdded[card.typeid]) {
                credit = extraAdded[card.typeid](card.dimension);
            }
            else if (credit) {
                if ((_q = cardSpace[card.typeid]) === null || _q === void 0 ? void 0 : _q.call(cardSpace, card.dimension)) {
                    credit = 0;
                }
                else {
                    credit -= 1;
                }
            }
        });
    }
    const loader = () => {
        return (!loadingError &&
            !isKeyboardIntent && (React.createElement("div", { className: "container" },
            React.createElement("div", { className: "plp-loading", "aria-live": "polite" },
                React.createElement("div", { className: "wrapper-loading" },
                    React.createElement("span", null, t('plp.loading')),
                    React.createElement("i", { className: "progress-bar", style: { width: `${((progress * 100) / count).toFixed(2)}%` } }))))));
    };
    const error = () => {
        if (loadingError && !isLoading) {
            return (React.createElement("div", { className: "container" },
                React.createElement("span", { className: "plp-error", "aria-live": "polite" }, t('plp.error'))));
        }
        return React.createElement(React.Fragment, null);
    };
    // const showBreadcrumb = () => {
    //   if (results.length && !hasMore) {
    //     return (
    //       <div className="container">
    //         <ErrorBoundary FallbackComponent={ErrorFallback}>
    //           <Breadcrumbs />
    //         </ErrorBoundary>
    //       </div>
    //     );
    //   }
    //   return <></>;
    // };
    const showAccessibleLoadMore = () => {
        if (results.length && hasMore && isKeyboardIntent) {
            return (React.createElement("div", { className: "container" },
                React.createElement("div", { className: "plp-a11y__loadmore" },
                    React.createElement("h2", null,
                        " ",
                        t('plp.moreproducts')),
                    React.createElement("button", { className: "button button--secondary", onClick: fetchData }, t('plp.loadmore')))));
        }
        return React.createElement(React.Fragment, null);
    };
    const schemaDataFc = (totalCards) => {
        // delete all the cards that not have name and description - OE-27154
        totalCards = totalCards.filter((card) => card.productName && card.shortDescription);
        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            url: location.href,
            numberOfItems: totalCards === null || totalCards === void 0 ? void 0 : totalCards.length,
            itemListElement: totalCards === null || totalCards === void 0 ? void 0 : totalCards.map((product) => {
                var _a, _b, _c, _d, _e;
                return ({
                    '@type': 'Product',
                    name: product.productName,
                    image: (_a = product.imgs) === null || _a === void 0 ? void 0 : _a.urls,
                    description: product === null || product === void 0 ? void 0 : product.shortDescription,
                    brand: {
                        '@type': 'Brand',
                        name: 'Moncler',
                    },
                    offers: {
                        url: product.productUrl,
                        '@type': 'Offer',
                        priceCurrency: (_c = (_b = product.price) === null || _b === void 0 ? void 0 : _b.sales) === null || _c === void 0 ? void 0 : _c.currency,
                        price: (_e = (_d = product.price) === null || _d === void 0 ? void 0 : _d.sales) === null || _e === void 0 ? void 0 : _e.value,
                        itemCondition: 'https://schema.org/NewCondition',
                        availability: getProductAvailability(product),
                    },
                });
            }),
        };
    };
    const schemaData = useMemo(() => (totalCards ? schemaDataFc(totalCards) : undefined), [totalCards]);
    const getBreadcrumbSchemaData = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: ++index,
                name: item.htmlValue,
                item: location.origin + item.url,
            })),
        };
    };
    const breadcrumbSchemaData = useMemo(() => (breadcrumbs ? getBreadcrumbSchemaData() : undefined), [breadcrumbs]);
    return (React.createElement(React.Fragment, null,
        schemaData && React.createElement(JsonLd, { data: schemaData }),
        breadcrumbSchemaData && React.createElement(JsonLd, { data: breadcrumbSchemaData }),
        (totalCards === null || totalCards === void 0 ? void 0 : totalCards.length) > 0 ? (React.createElement(InfiniteScroll, { dataLength: results.length, next: fetchData, hasMore: !isKeyboardIntent && hasMore, scrollThreshold: 0.5, loader: loader() },
            React.createElement("div", { role: "region" },
                React.createElement("ul", { className: classNames('grid-container', { 'look-expanded': expandedLook != undefined }, { 'has-look': results.find((item) => item.islook) }) }, totalCards === null || totalCards === void 0 ? void 0 : totalCards.map((product, i) => {
                    const DynamicCard = EditorialPromoCardTypeMap[product.typeid];
                    return (React.createElement("li", Object.assign({ key: i, className: classNames(expandedLook === product ? 'look-expanded' : product.isEditorial ? product.type : undefined, toAddMarginIndexes.has(i) ? toAddMarginIndexes.get(i) : '') }, (product.id && { 'aria-labelledby': product.id + '_link' })), !!DynamicCard ? (React.createElement(DynamicCard, { index: i, card: product, hasAnimation: landingAnimation, startAnimation: startAnimation })) : product.isEditorial ? (React.createElement(EditorialCard, { index: i, card: product, hasAnimation: landingAnimation, startAnimation: startAnimation, trackingAction: () => analytics.promoImage(category.name, product.title) })) : (React.createElement(ProductTile, { index: i, showLook: expandedLook === product, handleShopByLook: (expand) => (expand ? setExpandedLook(product) : setExpandedLook(undefined)), product: product, type: (product === null || product === void 0 ? void 0 : product.islook) ? 'PLPLOOK' : 'PLP', headingLevel: 'h2', hasAnimation: landingAnimation, startAnimation: startAnimation, category: category, isSimplified: true, withCarousel: window.tileCarousel && !(product === null || product === void 0 ? void 0 : product.islook), sortingrule: productSort === null || productSort === void 0 ? void 0 : productSort.ruleId }))));
                }))),
            showAccessibleLoadMore(),
            error())) : (React.createElement("p", { className: "primary-m text-center mt-48" }, t('search.noresults.title')))));
};
export default ListingResults;
